'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Resource {
  id: number;
  title: string;
  type: string;
  category: string;
  description: string;
  duration: string;
  format: string;
  thumbnail: string;
  thumbnailAlt: string;
}

const ResourceLibrary = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = ['All', 'Guided Meditations', 'Video Documentaries', 'Workbooks', 'Podcasts', 'Toolkits'];

  const resources: Resource[] = [
  {
    id: 1,
    title: 'Compassion Meditation for Beginners',
    type: 'Audio',
    category: 'Guided Meditations',
    description: 'A gentle introduction to compassion meditation, perfect for those new to the practice. Learn to extend kindness to yourself and others.',
    duration: '15 min',
    format: 'MP3',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_12e1a5c54-1768490226389.png",
    thumbnailAlt: 'Peaceful woman with closed eyes meditating in lotus position surrounded by soft natural light'
  },
  {
    id: 2,
    title: 'The Science of Forgiveness',
    type: 'Video',
    category: 'Video Documentaries',
    description: 'Explore the neuroscience and psychology behind forgiveness. Featuring interviews with leading researchers and real transformation stories.',
    duration: '42 min',
    format: 'MP4',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_17774508b-1766490596875.png",
    thumbnailAlt: 'Neuroscientist pointing at brain scan images on computer screen showing neural pathways'
  },
  {
    id: 3,
    title: '30-Day Transformation Workbook',
    type: 'Document',
    category: 'Workbooks',
    description: 'Complete preparation guide with daily reflection prompts, exercises, and tracking tools. Includes space for journaling your journey.',
    duration: '80 pages',
    format: 'PDF',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1510e191a-1766746389632.png",
    thumbnailAlt: 'Open journal with handwritten notes and pen on wooden desk with morning coffee'
  },
  {
    id: 4,
    title: 'Stories of Reconciliation',
    type: 'Podcast',
    category: 'Podcasts',
    description: 'Hear firsthand accounts from people who chose forgiveness after unimaginable pain. Inspiring stories of healing and transformation.',
    duration: '35 min',
    format: 'MP3',
    thumbnail: "https://images.unsplash.com/photo-1701491332798-35f53bc3e9c0",
    thumbnailAlt: 'Professional podcast recording setup with microphone and headphones in warm studio lighting'
  },
  {
    id: 5,
    title: 'Community Organizing Toolkit',
    type: 'Document',
    category: 'Toolkits',
    description: 'Everything you need to start a local SIG Day (The Global No Enemy Festival) chapter. Includes event planning templates, marketing materials, and facilitation guides.',
    duration: '120 pages',
    format: 'PDF',
    thumbnail: "https://images.unsplash.com/photo-1557734864-c78b6dfef1b1",
    thumbnailAlt: 'Community organizers collaborating around table with planning documents and colorful sticky notes'
  },
  {
    id: 6,
    title: 'Advanced Loving-Kindness Practice',
    type: 'Audio',
    category: 'Guided Meditations',
    description: 'Deepen your practice with this extended meditation for difficult relationships. Includes techniques for working with resistance.',
    duration: '30 min',
    format: 'MP3',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_155a316f2-1765132774555.png",
    thumbnailAlt: 'Serene meditation space with cushions candles and soft natural light through window'
  },
  {
    id: 7,
    title: 'Global Peace Movement Documentary',
    type: 'Video',
    category: 'Video Documentaries',
    description: 'Follow SIG Day (The Global No Enemy Festival) from its inception to global phenomenon. Witness celebrations from 50+ countries and hear founder insights.',
    duration: '58 min',
    format: 'MP4',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_12aa5b9cf-1771168758268.png",
    thumbnailAlt: 'Aerial view of massive crowd gathering in city square with colorful banners and peace symbols'
  },
  {
    id: 8,
    title: 'Forgiveness in Action Podcast Series',
    type: 'Podcast',
    category: 'Podcasts',
    description: 'Six-episode series exploring forgiveness across cultures, religions, and conflict zones. Features experts and practitioners worldwide.',
    duration: '6 episodes',
    format: 'MP3',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_17d768606-1765321342876.png",
    thumbnailAlt: 'Diverse group of people wearing headphones engaged in podcast discussion around table'
  }];


  const filteredResources = selectedCategory === 'All' ?
  resources :
  resources.filter((r) => r.category === selectedCategory);

  const getTypeIcon = (type: string): string => {
    const icons: Record<string, string> = {
      'Audio': 'MusicalNoteIcon',
      'Video': 'VideoCameraIcon',
      'Document': 'DocumentTextIcon',
      'Podcast': 'MicrophoneIcon'
    };
    return icons[type] || 'DocumentIcon';
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-primary mb-4">
              Resource Library
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              Loading resources...
            </p>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-primary mb-4">
            Resource Library
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Access guided meditations, video documentaries, workbooks, podcasts, and organizing toolkits to support your transformation journey.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) =>
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-6 py-2 rounded-full font-cta text-sm font-bold transition-all duration-300 ${
            selectedCategory === category ?
            'bg-primary text-primary-foreground shadow-md' :
            'bg-card text-foreground hover:bg-muted'}`
            }>

              {category}
            </button>
          )}
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredResources.map((resource) =>
          <div
            key={resource.id}
            className="bg-card rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">

              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={resource.thumbnail}
                alt={resource.thumbnailAlt}
                className="w-full h-full object-cover" />

                <div className="absolute top-4 right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <Icon name={getTypeIcon(resource.type) as any} size={24} className="text-primary-foreground" />
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-muted rounded-full text-xs font-body text-muted-foreground">
                    {resource.category}
                  </span>
                  <span className="text-xs text-secondary font-body">
                    {resource.duration}
                  </span>
                </div>
                <h3 className="text-xl font-headline font-bold text-primary mb-2">
                  {resource.title}
                </h3>
                <p className="text-sm text-foreground font-body leading-relaxed mb-4">
                  {resource.description}
                </p>
                <div className="flex gap-3">
                  <button className="flex-1 px-4 py-2 bg-accent text-accent-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105">
                    Access
                  </button>
                  <button className="px-4 py-2 bg-muted text-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-background">
                    <Icon name="ArrowDownTrayIcon" size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default ResourceLibrary;